import React, { ChangeEvent, useContext, useMemo } from 'react';
import DataTable from '../../../../../../common/components/DataTable';
import { IDataTableColumn } from '../../../../../../common/types/dataTable';
import { useTranslation } from 'react-i18next';
import { Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../../template/store';
import {
  removeDocument,
  updateLinerVerify,
  updateListByRowNumber,
  updateSelectedAttachmentFilePath,
  updateSelectedAttachmentName,
  updateSelectedFileServerPath,
} from '../../../instructionSlice/DOExtensionSlice';
import moment from 'moment';
import { getDateTimeFormat } from '../../../../../../../helpers/dateFormat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import UploadThing from '../../../../../../../components/common/UploadThing';
import useBlCountry from '../../../../../../../hooks/useBlCountry';
import { useLinerConfig } from '../../../../../../../hooks/useLinerConfig';
import { ReleaseInstructionConfigContext } from '../../DoExtension';
import { encryptAndEncode } from '../../../../../../../encrypt-util';
import { openDocumentInNewTab } from '../../../../../../../helpers/openDocumentInNewTab';

interface ReleaseAttachmentDataTableProps {
  doExtension: string;
  accessGroupLevel: string[];
  loadingFileState: { [key: string]: number };
  setLoadingFileState: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>;
  disableFields: boolean;
}

const ReleaseAttachmentDataTable: React.FC<ReleaseAttachmentDataTableProps> = ({
  doExtension,
  accessGroupLevel,
  loadingFileState,
  setLoadingFileState,
  disableFields,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const docList = useSelector((state: RootState) => state.doExtension.docList);
  const extensionDocList = useSelector((state: RootState) => state.doExtension.extensionDocList);
  const extensionAttData = useSelector((state: RootState) => state.doExtension.extensionAttData);
  const dropdownDocList = useSelector((state: RootState) => state.doExtension.dropdownDocList);
  const docAttachment = useSelector((state: RootState) => state.doExtension.attData);
  const profileDetails = useSelector((state: RootState) => state.userProfile.profile);
  const selectedAttachmentName = useSelector(
    (state: RootState) => state.doExtension.selectedAttachmentName
  );
  const selectedAttachmentFilePath = useSelector(
    (state: RootState) => state.doExtension.selectedAttachmentFilePath
  );
  const serverFilePath = useSelector(
    (state: RootState) => state.doExtension.selectedFileServerPath
  );
  const selectedLinerId = useSelector((state: RootState) => state.navbarTop.linerId);
  const demurrageViewForm = useSelector(
    (state: RootState) => state.doExtension.demurrageFormView
  );
  const demurrageForm = useSelector((state: RootState) => state.doExtension.demurrageFormView);
  const blCountry = useBlCountry('view', 'import', 820)[0];
  
  const currentDocList = extensionDocList ?? [];

  const hasActiveUploads = useMemo(
    () =>
      Object.values(loadingFileState).some(
        (val) => val != 100 && val != -1 && val != undefined
      ),
    [loadingFileState]
  );
  const releaseInstructionConfig=useContext(ReleaseInstructionConfigContext);
  const getDocumentLink = (
    dndInstructionAttId: number | null | undefined,
    moduleType: string = 'DND'
  ): string | undefined => {
    if (!dndInstructionAttId) return undefined;

    const linerIdValue =
      profileDetails.partnerType === 'liner' ? profileDetails?.partnerId : selectedLinerId;

    return `${process.env.REACT_APP_CLIENT_APP_HOST}/api/edo-rest-v2/v2/common/openDocument?id=${encryptAndEncode(dndInstructionAttId)}&moduleTp=${moduleType}&linerId=${encryptAndEncode(linerIdValue)}&rd=N`;
  };

  const charLimit = (name: string | undefined, limit: number): string => {
    if (!name) return '';
    return name.length >= limit ? name.substring(0, limit) + '...' : name;
  };

  const uploadDocument = (rowData: any) => {
    const {
      docId,
      dndInstructionAttId,
      fileName,
      isVerify,
      rowNumber
    } = rowData.row.original;

    const key = dndInstructionAttId ?? rowNumber ?? null;

    if (!disableFields) {
      return (
        <div className="d-flex justify-content-start align-items-center">
          <UploadThing
            name={
              selectedAttachmentFilePath?.[key]
                ? fileName
                : selectedAttachmentName?.[key]?.name
            }
            loading={loadingFileState[key]}
            disabled={
              !!isVerify ||
              hasActiveUploads 
              // ||
              // (docId == 309 && dndInstructionAttId)
              // (dndInstructionAttId)

            }
            fulfilled={(name: string, serverPath: string, serverName: string): void => {
              setLoadingFileState((state) => ({
                ...state,
                [key]: 100,
              }));
              dispatch(
                updateSelectedFileServerPath({
                  ...serverFilePath,
                  [key]: [serverPath, serverName],
                })
              );
      
              dispatch(
                updateSelectedAttachmentName({
                  ...selectedAttachmentName,
                  [key]: { name },
                })
              );
            }}
            failed={(): void => {
              setLoadingFileState((state) => ({
                ...state,
                [key]: -1,
              }));
              dispatch(
                updateSelectedAttachmentName({
                  ...selectedAttachmentName,
                  [key]: { name: '' },
                })
              );
            }}
            pending={(loading: number): void => {
              setLoadingFileState((state) => ({
                ...state,
                [key]: loading,
              }));
            }}
            clear={() => {}}
            selected={(name: string): void => {
              dispatch(
                updateSelectedAttachmentName({
                  ...selectedAttachmentName,
                  [key]: { name },
                })
              );
              setLoadingFileState((state) => ({
                ...state,
                [key]: 0,
              }));
              dispatch(
                updateSelectedAttachmentFilePath({
                  ...selectedAttachmentFilePath,
                  [key]: null,
                })
              );
            }}
            fileLimit={10}
            fileType={['PDF']}
            labelWidth="250px"
            // link={getDocumentLink(dndInstructionAttId)}
            onOpenDocument={
              !selectedAttachmentFilePath?.[key] || !dndInstructionAttId
                ? undefined
                : async () => {
                    await openDocumentInNewTab({
                      url: '/api/edo-rest-v2/v2/common/openDocument',
                      params: {
                        id: encryptAndEncode(dndInstructionAttId),
                        moduleTp: 'DND',
                        linerId: encryptAndEncode(
                          profileDetails.partnerType === 'liner'
                            ? profileDetails?.partnerId
                            : selectedLinerId
                        ),
                      },
                    });
                  }
            }
            allowRemove={false}
          />
        </div>
      );
    } else {
      return (
        <div className="d-flex justify-content-start align-items-center">
          {/* PIL MZ - New Feature: Customer view multiple documents */}
          {/* {releaseInstructionConfig?.fields?.allowMultipleDocumentUpload && doExtension === 'Y'
            ? (extensionAttData.list ?? []).map(
                (doc: any, index: number) => (
                  <span
                    key={index}
                    className="text-primary cursor-pointer d-block"
                    onClick={() => {
                      const link = getDocumentLink(doc.dndInstructionAttId);
                      if (link) {
                        window.open(link, '_blank');
                      }
                    }}
                  >
                    {charLimit(doc.fileName, 50) || `Document ${index + 1}`}
                  </span>
                )
              )
            :  */}
            {/* ( */}
              <span
                className="text-primary cursor-pointer"
								onClick={async () => {
								  await openDocumentInNewTab({
								    url: '/api/edo-rest-v2/v2/common/openDocument',
								    params: {
								      id: encryptAndEncode(dndInstructionAttId),
								      moduleTp: 'DND',
								      linerId: encryptAndEncode(
								        profileDetails.partnerType === 'liner'
								          ? profileDetails?.partnerId
								          : selectedLinerId
								      ),
								    },
								  });
								}}
              >
                {charLimit(fileName, 50)}
              </span>
            {/* )} */}
        </div>
      );
    }
  };

  const TableRemoveDoc = (rowData: any) => {
    const {
      isVerify,
      isDropDownDoc,
      rowNumber,
      dndInstructionAttId,
    } = rowData.row.original;

    if (isDropDownDoc) {
      return (
        <div className="d-flex justify-content-start align-items-center">
          {!disableFields && !dndInstructionAttId && (
            <div>
              <Button
                variant="falcon-warning"
                style={{
                  background: 'transparent',
                  border: 'none',
                  boxShadow: 'none',
                  padding: '0px',
                }}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  dispatch(
                    removeDocument({ rowNumber: rowNumber, isExtension: true })
                  );
                  const { [rowNumber]: removedLoadingState, ...restLoadingState } =
                    loadingFileState ?? {};
                  const newLoadingFileState =
                    Object.keys(restLoadingState).length > 0 ? restLoadingState : {};
                  setLoadingFileState(newLoadingFileState);
                }}
                disabled={
                  !isDropDownDoc ||
                  !!isVerify ||
                  hasActiveUploads ||
                  !!dndInstructionAttId
                }
              >
                <FontAwesomeIcon className="me-1" icon={faTrash} />
              </Button>
            </div>
          )}
        </div>
      );
    }
  };

  const TableLinerId = (rowData: any) => {
    const { actionBy } = rowData.row.original;
    return (
      <div className="d-flex justify-content-start align-items-center">
        <span>{actionBy}</span>
      </div>
    );
  };

  const TableLastUpdated = (rowData: any) => {
    const { actionOn } = rowData.row.original;
    return (
      <div className="d-flex justify-content-start align-items-center">
        <span>{actionOn}</span>
      </div>
    );
  };

  const TableVerifyDoc = (rowData: any) => {
    const { docId, isVerify, fileName } = rowData.row.original;

    if (!fileName) return null;

    const existingAtt = demurrageForm.attList?.find((i: any) => i.docId === docId);
    const isCurrentlyVerified = existingAtt?.isVerify ?? false;

    const isDisabled =
      profileDetails.partnerType !== 'liner' ||
      !(accessGroupLevel[demurrageViewForm.groupLevel] === 'Y') ||
      (demurrageViewForm.status === 'Completed' ||
      demurrageViewForm.status?.includes('Rejected'));

    return (
      <div className="d-flex justify-content-start align-items-center">
        <input
          type="checkbox"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            const isChecked = e.target.checked;
            dispatch(
              updateLinerVerify({
                verify: isChecked,
                docId: docId,
                fileName: fileName,
                actionBy:
                  isChecked === isCurrentlyVerified
                    ? existingAtt?.actionBy
                    : profileDetails.email,
                actionOn:
                  isChecked === isCurrentlyVerified
                    ? existingAtt?.actionOn
                    : moment(moment.utc(new Date()).toDate()).format(
                        getDateTimeFormat().toLocaleUpperCase()
                      ),
              })
            );
          }}
          checked={isVerify}
          disabled={isDisabled}
        />
      </div>
    );
  };

  const docAttachmentColumns: IDataTableColumn[] = [
    {
      accessor: 'dndInstructionAttId',
      Header: 'dndInstructionAttId',
    },
    {
      accessor: 'docId',
      Header: t('resource_2:documentName'),
      Cell: (rowData: any) => {
        const { docId, isDropDownDoc, rowNumber, isVerify, dndInstructionAttId } =
          rowData.row.original;
        const name = currentDocList?.find((v: any) => v?.id === docId)?.name;

        if (isDropDownDoc && !dndInstructionAttId) {
          return (
            <div className="d-flex flex-row gx3">
              <span className="mandatory">*</span>
              <Form.Select
                onChange={(e) => {
                  const selectedId = Number(e.target.value);
                  const selectedOption = dropdownDocList?.find(
                    (option) => option.id === selectedId
                  );

                  if (!selectedOption) return;

                  dispatch(
                    updateListByRowNumber({
                      rowNumber: rowNumber,
                      updatedFields: {
                        id: selectedOption.id,
                        name: selectedOption.name,
                      },
                      isExtension: true,
                    })
                  );
                }}
                value={docId !== null && docId !== undefined ? String(docId) : '0'}
                className="fs--1 form-control form-select"
                disabled={
                  !!isVerify || hasActiveUploads || !!dndInstructionAttId
                }
              >
                <option value="0" disabled>
                  Select a document
                </option>
                {dropdownDocList?.map((option: any) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          );
        }

        return (
          <>
            {currentDocList?.find((v: any) => v?.id === docId)?.m === true && (
              <span className="mandatory">*</span>
            )}
            <span title={name || ''}>{name || ''}</span>
          </>
        );
      },
      width: 200,
      maxWidth: 180,
    },
    {
      accessor: 'byteArray',
      Header: t('resource_1:upload'),
      Cell: uploadDocument,
      width: 350,
      maxWidth: 350,
    },
    {
      accessor: 'remove',
      Header: '',
      Cell: TableRemoveDoc,
      width: 50,
      maxWidth: 50,
    },
    {
      accessor: 'verify',
      Header: 'Verify',
      Cell: TableVerifyDoc,
      width: 50,
      maxWidth: 50,
    },
    {
      accessor: 'linerEmaiId',
      Header: 'Liner ID',
      Cell: TableLinerId,
      width: 150,
      maxWidth: 150,
    },
    {
      accessor: 'lastUpdatedId',
      Header: 'Last Updated ',
      Cell: TableLastUpdated,
      width: 180,
      maxWidth: 180,
    },
    {
      accessor: 'fileName',
      Header: 'fileName',
    },
  ];

  const staticHiddenCols = accessGroupLevel.includes('Y')
    ? ['dndInstructionAttId', 'fileName', 'remove']
    : ['dndInstructionAttId', 'fileName', 'verify', 'linerEmaiId', 'lastUpdatedId'];

  return (
    <div className="col-lg-12 mt-2 mb-2 demmurageTable">
      <DataTable
        data={ extensionAttData.list ?? []}
        gridName="RELEASE_ATTACHMENT_DO_EXTENSION"
        columns={docAttachmentColumns}
        sortable={false}
        searchable={false}
        showColumnFilter={false}
        selection={false}
        perPage={100}
        pagination={false}
        showExport={false}
        uniqueFilterKey="releaseAttachment"
        autoHeight={true}
        staticHiddenCols={staticHiddenCols}
      />
    </div>
  );
};

export default ReleaseAttachmentDataTable;
